/** The rolls a condition's `rolls` (docs/MRCS/dnd-5e.md §3.4) can name, and the targets `combat.condition_reads` can add beyond `attack` and `ac`. */
export const CONDITION_ROLLS = ['attack', 'save', 'ability_check', 'skill', 'initiative'];
export const CONDITION_READS = ['save', 'ability_check', 'skill', 'initiative', 'speed', 'levels'];
/** A condition's own slug as the site allows one (an entry's, homebrew too), and how many a condition may include. */
const IMPLIED_SLUG_RE = /^[a-z0-9][a-z0-9._-]{0,199}$/;
export const MAX_IMPLIED = 8;
export const ONE_ATTACK_TARGETS = ['weapon_dice', 'weapon_damage'];
export const EFFECT_MOD_TARGETS = ['attack', 'ac', 'damage', 'save', ...ONE_ATTACK_TARGETS];
/** What a mod changes, as words: an effect's badge and the log's line. */
export const modTargetWords = (t) => (t === 'weapon_dice' ? "weapon's dice, once" : t === 'weapon_damage' ? "weapon's damage, once" : t);
/** `type`: a bonus type's key, as long as a system's stacking table may name one (the site's key, `parseStacking`). */
export const EFFECT_MOD_LIMITS = { mods: 4, value: 50, type: 63 };
/** A bonus type as a key of the system's stacking table: lower case, spaces as `_` ("natural armor" → `natural_armor`). */
export const bonusTypeKey = (t) => t.trim().toLowerCase().replace(/[\s-]+/g, '_');
/** A creature an effect's number counts against (issue 1508): a token's id. */
const VS_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TYPE_RE = new RegExp(`^[a-z][a-z0-9_]{0,${EFFECT_MOD_LIMITS.type - 1}}$`);
/** The bonus types an effect may name in this system: its stacking table's, in its order. None without one. */
export const bonusTypes = (stacking) => (stacking ? Object.keys(stacking.types) : []);
export function parseEffectMods(v) {
    if (!Array.isArray(v) || v.length > EFFECT_MOD_LIMITS.mods)
        return null;
    const out = [];
    for (const m of v) {
        if (typeof m !== 'object' || m === null)
            return null;
        const { target, value, type, only, vs, once, which } = m;
        if (!EFFECT_MOD_TARGETS.includes(target) || typeof value !== 'number' || !Number.isInteger(value) || value === 0 || Math.abs(value) > EFFECT_MOD_LIMITS.value)
            return null;
        // Any well-formed type: whether it is one of the system's is checked as the effect is committed (issue 1303).
        const key = typeof type === 'string' ? bonusTypeKey(type) : undefined;
        if (type !== undefined && (key === undefined || !TYPE_RE.test(key)))
            return null;
        if (only !== undefined && only !== 'melee' && only !== 'ranged')
            return null;
        if (vs !== undefined && !(typeof vs === 'string' && VS_RE.test(vs)))
            return null;
        if (once !== undefined && once !== true)
            return null;
        if (which !== undefined && !(typeof which === 'string' && target === 'save' && which.length >= 1 && which.length <= 40))
            return null;
        out.push({ target: target, value, ...(typeof which === 'string' ? { which } : {}), ...(key !== undefined && key !== 'untyped' ? { type: key } : {}), ...(only ? { only: only } : {}), ...(typeof vs === 'string' ? { vs } : {}), ...(once ? { once: true } : {}) });
    }
    return out;
}
const num = (v) => (typeof v === 'number' && Number.isInteger(v) && Math.abs(v) <= 100 ? v : null);
/**
 * A condition's effects as the site holds them (`attrs.effects` of a condition entry; docs/MRCS/condition-effects.md),
 * read for the attack: its modifiers to `attack` (its own, or `side: 'against'` for attacks against it) and to `ac`,
 * each for the `reach` it names (none is both), `loses_dex_bonus_to_ac`, and the conditions it `implies`. A `situational` number holds only in a
 * situation the table cannot know and is left to the DM. Anything else (skills, speed, saves, damage) is not the
 * attack's; a shape it cannot read gives nothing, never a guess.
 */
export function conditionMods(name, effects) {
    const out = { name, attack: [], ac: [], againstIt: [], losesDex: false };
    if (typeof effects !== 'object' || effects === null)
        return out;
    const e = effects;
    if (typeof e.per_count === 'object' && e.per_count !== null)
        out.perCount = conditionMods(name, e.per_count);
    if (Array.isArray(e.by_count)) {
        const tiers = e.by_count.filter((t) => Number.isInteger(t?.at) && t.at >= 1).map((t) => ({ at: t.at, mods: conditionMods(name, t) }));
        if (tiers.length)
            out.byCount = tiers;
    }
    if (Number.isInteger(e.dies_at_count) && e.dies_at_count >= 1)
        out.diesAtCount = e.dies_at_count;
    out.losesDex = e.loses_dex_bonus_to_ac === true;
    if (Array.isArray(e.implies)) {
        const implies = [...new Set(e.implies.filter((x) => typeof x === 'string' && IMPLIED_SLUG_RE.test(x)))].slice(0, MAX_IMPLIED);
        if (implies.length)
            out.implies = implies;
    }
    const rolls = conditionRolls(e.rolls);
    if (rolls.length)
        out.rolls = rolls;
    const autoFail = Array.isArray(e.auto_fail) ? e.auto_fail.filter((k) => typeof k === 'string') : [];
    if (autoFail.length)
        out.autoFail = autoFail;
    const within = e.auto_critical?.within;
    if (typeof within === 'number' && Number.isInteger(within) && within >= 1)
        out.autoCritical = { within };
    if (e.no_actions === true)
        out.noActions = true;
    if (e.ends_concentration === true)
        out.endsConcentration = true;
    for (const m of Array.isArray(e.modifiers) ? e.modifiers : []) {
        const r = (m ?? {});
        if (r.target === 'speed' && r.situational !== true && typeof r.value === 'number' && (r.side ?? 'own') === 'own') {
            if (r.kind === 'multiplier' && r.value >= 0 && r.value <= 10)
                (out.speed ??= []).push({ value: r.value, times: true });
            else if (r.kind === 'modifier' && num(r.value) !== null)
                (out.speed ??= []).push({ value: r.value });
            continue;
        }
        const value = num(r.value);
        if (r.kind !== 'modifier' || value === null || value === 0 || r.situational === true)
            continue;
        if (r.reach !== undefined && r.reach !== 'melee' && r.reach !== 'ranged')
            continue;
        const side = r.side ?? 'own';
        const mod = { value, ...(typeof r.bonus_type === 'string' ? { type: r.bonus_type } : {}), ...(r.reach ? { only: r.reach } : {}) };
        if ((r.target === 'save' || r.target === 'ability_check' || r.target === 'skill' || r.target === 'initiative') && side === 'own')
            (out.reads ??= []).push({ ...mod, target: r.target, ...(typeof r.which === 'string' ? { which: r.which } : {}) });
        if (r.target === 'attack' && side === 'own')
            out.attack.push(mod);
        else if (r.target === 'attack' && side === 'against')
            out.againstIt.push(mod);
        else if (r.target === 'ac' && side === 'own')
            out.ac.push(mod);
    }
    return out;
}
/**
 * A condition at its level (docs/MRCS/dnd-5e.md §3.4), in a system whose `condition_reads` names `levels`: every level's
 * numbers it has reached, added up, and each one's numbers times the level. `Exhaustion 2` is its name then, so the
 * working says the level. What `implies` a level adds is read by whoever reads `implies`.
 */
export function atCount(c, level) {
    const { perCount, byCount, ...base } = c;
    const times = (m) => {
        const by = (x) => ({ ...x, value: x.value * level });
        return { ...m, attack: m.attack.map(by), ac: m.ac.map(by), againstIt: m.againstIt.map(by), ...(m.damage ? { damage: m.damage.map(by) } : {}), ...(m.reads ? { reads: m.reads.map(by) } : {}), ...(m.speed ? { speed: m.speed.map((s) => (s.times ? { ...s, value: s.value ** level } : { ...s, value: s.value * level })) } : {}) };
    };
    const parts = [...(perCount ? [times(perCount)] : []), ...(byCount ?? []).filter((t) => t.at <= level).sort((a, b) => a.at - b.at).map((t) => t.mods)];
    const out = parts.reduce((a, p) => ({
        ...a, attack: [...a.attack, ...p.attack], ac: [...a.ac, ...p.ac], againstIt: [...a.againstIt, ...p.againstIt],
        ...(p.damage ? { damage: [...(a.damage ?? []), ...p.damage] } : {}), ...(p.reads ? { reads: [...(a.reads ?? []), ...p.reads] } : {}),
        ...(p.speed ? { speed: [...(a.speed ?? []), ...p.speed] } : {}), ...(p.rolls ? { rolls: [...(a.rolls ?? []), ...p.rolls] } : {}),
        ...(p.implies ? { implies: [...new Set([...(a.implies ?? []), ...p.implies])].slice(0, MAX_IMPLIED) } : {}),
    }), { ...base, name: level > 1 ? `${c.name} ${level}` : c.name });
    return { ...out, ...(c.diesAtCount !== undefined && level >= c.diesAtCount ? { fatal: true } : {}) };
}
/** A condition's `rolls` as the site holds them: what cannot be read is left out, never a guess. */
function conditionRolls(v) {
    if (!Array.isArray(v))
        return [];
    return v.flatMap((x) => {
        const r = (x ?? {});
        if (!CONDITION_ROLLS.includes(r.on) || (r.with !== 'advantage' && r.with !== 'disadvantage'))
            return [];
        if (r.reach !== undefined && r.reach !== 'melee' && r.reach !== 'ranged')
            return [];
        if (r.within !== undefined && !(Number.isInteger(r.within) && r.within >= 1))
            return [];
        return [{ on: r.on, with: r.with, ...(r.side === 'against' ? { against: true } : {}), ...(typeof r.which === 'string' ? { which: r.which } : {}), ...(r.reach ? { only: r.reach } : {}), ...(typeof r.within === 'number' ? { within: r.within } : {}) }];
    });
}
/**
 * The sources a roll has from what the creatures carry (docs/MRCS/dnd-5e.md §3.2.2 item 1): the roller's own lines, and
 * the other creature's lines about rolls against it, which are about the target. A line with `within` and no known
 * distance does not count.
 */
export function conditionSources(on, own, other, at = {}) {
    const fits = (r) => r.on === on && (!r.which || r.which === at.which) && (!r.only || r.only === at.reach) && (r.within === undefined || (typeof at.apart === 'number' && at.apart <= r.within));
    const out = [
        ...own.flatMap((c) => (c.rolls ?? []).filter((r) => !r.against && fits(r)).map((r) => ({ with: r.with, name: c.name }))),
        ...other.flatMap((c) => (c.rolls ?? []).filter((r) => r.against && fits(r)).map((r) => ({ with: r.with, name: c.name, aboutTarget: true }))),
    ];
    return out.filter((s, i) => out.findIndex((x) => x.with === s.with && x.name === s.name) === i).slice(0, 15);
}
/** A condition's numbers on one roll, stacked by type, as terms: only called for a target the system reads. */
export const conditionTerms = (mods, target, which, stacking) => stack(mods.flatMap((c) => (c.reads ?? []).filter((m) => m.target === target && (!m.which || m.which === which)).map((mod) => ({ label: c.name, mod }))), stacking);
/** What speed comes to under the conditions: each adds, then each multiplies. */
export function conditionSpeed(speed, mods) {
    const all = mods.flatMap((c) => c.speed ?? []);
    const added = all.filter((m) => !m.times).reduce((n, m) => n + m.value, speed);
    return Math.max(0, all.filter((m) => m.times).reduce((n, m) => n * m.value, added));
}
/**
 * A tracker effect's numbers as a condition's: its label is the name, its attack, AC and damage numbers the same terms.
 * `other`, the creature on the attack's other side: a number `vs` one creature (issue 1508) counts only when it is that one.
 */
export function effectAsMods(label, mods, other) {
    const counts = mods.filter((m) => m.vs === undefined || m.vs === other);
    const as = (m) => ({ value: m.value, ...(m.type ? { type: m.type } : {}), ...(m.only ? { only: m.only } : {}) });
    const damage = counts.filter((m) => m.target === 'damage').map(as);
    const again = counts.filter((m) => ONE_ATTACK_TARGETS.includes(m.target)).map((m) => ({ value: m.value, whole: m.target === 'weapon_damage', ...(m.only ? { only: m.only } : {}) }));
    return {
        name: label,
        attack: counts.filter((m) => m.target === 'attack').map(as),
        ac: counts.filter((m) => m.target === 'ac').map(as),
        againstIt: [],
        losesDex: false,
        ...(damage.length ? { damage } : {}),
        ...(again.length ? { again } : {}),
    };
}
/**
 * A combatant's effects once it has made an attack of this reach: each number that counts for one attack gone, and a
 * rule's effect left with no numbers named "<label>: used". Null when nothing was spent.
 */
export function spentOnAttack(effects, reach) {
    const spends = (m) => (m.once === true || ONE_ATTACK_TARGETS.includes(m.target)) && (!m.only || m.only === reach);
    if (!effects.some((e) => e.mods?.some(spends)))
        return null;
    return effects.flatMap((e) => {
        if (!e.mods?.some(spends))
            return [e];
        const left = e.mods.filter((m) => !spends(m));
        if (left.length)
            return [{ ...e, mods: left }];
        if (!e.rule)
            return [];
        const { mods: _spent, ...rest } = e;
        return [{ ...rest, label: `${e.label}: used` }];
    });
}
/**
 * The system's stacking rule over named modifiers: a type that stacks adds up; of any other only the strongest bonus
 * and the worst penalty count, and what does not count is left out of the terms. A modifier with no type is the table's
 * `untyped`. A system with no stacking table removes nothing: every term counts, and the DM's line shows them all.
 */
export function stack(named, stacking) {
    const out = [];
    const best = new Map();
    const term = (n) => ({ label: n.label, value: n.mod.value, ...(n.aboutTarget ? { aboutTarget: true } : {}) });
    for (const n of named) {
        const type = n.mod.type ? bonusTypeKey(n.mod.type) : 'untyped';
        if (!stacking || (stacking.types[type] ?? stacking.default) === 'stack') {
            out.push(term(n));
            continue;
        }
        const key = `${type} ${n.mod.value > 0 ? '+' : '-'}`;
        const have = best.get(key);
        if (!have || Math.abs(n.mod.value) > Math.abs(have.mod.value))
            best.set(key, n);
    }
    for (const n of best.values())
        out.push(term(n));
    return out;
}
/**
 * The attack's terms from what the two sides carry (§5): the attacker's own (its conditions and effects on its attack
 * rolls; a target's "attacks against it" conditions, such as helpless), and the target's AC changes — each for melee
 * or ranged as it says — stacked by type. `losesDex` asks for the flat-footed AC.
 */
export function situationTerms(attacker, target, reach, stacking) {
    const fits = (m) => !m.only || m.only === reach;
    const attack = stack([
        ...attacker.flatMap((c) => c.attack.filter(fits).map((mod) => ({ label: c.name, mod }))),
        ...target.flatMap((c) => c.againstIt.filter(fits).map((mod) => ({ label: `vs ${c.name.toLowerCase()}`, mod, aboutTarget: true }))),
    ], stacking);
    const ac = stack(target.flatMap((c) => c.ac.filter(fits).map((mod) => ({ label: c.name, mod }))), stacking);
    // The attacker's own damage numbers (issue 1508: Power Attack), for this reach, stacked as its attack numbers are.
    const damage = stack(attacker.flatMap((c) => (c.damage ?? []).filter(fits).map((mod) => ({ label: c.name, mod }))), stacking);
    const again = attacker.flatMap((c) => (c.again ?? []).filter((m) => !m.only || m.only === reach).map((m) => ({ label: c.name, value: m.value, whole: m.whole })));
    return { attack, ac, damage, again, losesDex: target.find((c) => c.losesDex)?.name ?? null };
}
//# sourceMappingURL=modifiers.js.map